import { CandlestickChart, Globe2, Rocket, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeader, scrollToSection } from "./Shared";
import { IMAGES } from "@/lib/site";

const MARKETS = [
  {
    icon: CandlestickChart,
    name: "Bursa Malaysia",
    tag: "Home Market",
    details: "KLCI equities, mid-cap growth, Syariah-compliant IPO and intra-contra swing setups.",
    instruments: ["KLCI Blue Chips", "Syariah Mid-Caps", "IPO Allocations", "FCPO & FKLI"],
    target: "RM 300 — RM 10,000 / trade",
    image: IMAGES.tradingScreen,
  },
  {
    icon: Globe2,
    name: "Forex & Comex",
    tag: "Global Sessions",
    details: "XAU/USD gold, EUR/USD and GBP/JPY high-probability London and New York session plays.",
    instruments: ["XAU/USD Gold", "Major FX Pairs", "Silver & Crude", "London / NY Sessions"],
    target: "30 — 150 pips per setup",
    image: IMAGES.analytics,
  },
  {
    icon: Rocket,
    name: "SGX & Nasdaq / HSI",
    tag: "Alpha Momentum",
    details: "Hang Seng index futures, US tech growth leaders and SGX dividend stalwarts.",
    instruments: ["HSI Futures", "Nasdaq Tech", "SGX Mainboard", "Momentum Breakouts"],
    target: "High-alpha momentum playbook",
    image: IMAGES.trader,
  },
];

export default function Markets() {
  return (
    <section id="markets" data-testid="markets-section" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Markets Covered"
          title="Three desks. One standard of precision."
          sub="Every plan tier runs across all three market desks — Bursa, Forex & Comex, and SGX & Nasdaq — so your capital is never tied to a single session."
        />

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {MARKETS.map((m, idx) => (
            <Reveal key={m.name} delay={idx * 0.12} className="col-span-full md:col-span-4">
              <article
                data-testid={`market-card-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-surface transition-all duration-500 hover:-translate-y-2 hover:border-gold/40"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className="spotlight-img h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full border border-gold/40 bg-obsidian/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur">
                    {m.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3">
                    <m.icon size={22} className="text-alpha" />
                    <h3 className="font-display text-xl font-bold text-slate-100">{m.name}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{m.details}</p>
                  <ul className="mt-4 grid grid-cols-2 gap-2">
                    {m.instruments.map((ins) => (
                      <li key={ins} className="rounded-md border border-line bg-panel px-2.5 py-1.5 font-mono text-[10px] text-slate-400">
                        {ins}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex items-center justify-between pt-6">
                    <span className="font-mono text-xs font-semibold text-alpha">{m.target}</span>
                    <button
                      data-testid={`market-explore-${idx}`}
                      onClick={() => scrollToSection("#pricing")}
                      className="flex items-center gap-1 text-xs font-semibold text-slate-300 transition-colors hover:text-gold"
                    >
                      View plans <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
